import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DefaultPageLayout } from '../components/layouts/DefaultPageLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar } from '../components/ui/avatar';
import { IconWithBackground } from '../components/ui/icon-with-background';
import { DropdownMenu } from '../components/ui/dropdown-menu';
import BunnyLoader from '../components/ui/BunnyLoader';
import { 
  FeatherArrowLeft, 
  FeatherCalendar, 
  FeatherDownload,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherUser,
  FeatherClock
} from '../components/ui/icons';
import { getTimeOffsData } from '../services/managerService';
import './TimeOffs.css';

const TimeOffs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeOffsData, setTimeOffsData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [totalAbsences, setTotalAbsences] = useState(0);
  const [totalHolidays, setTotalHolidays] = useState(0);
  const [totalApprovedLeaves, setTotalApprovedLeaves] = useState(0);
  const [totalLOP, setTotalLOP] = useState(0);

  // Initial load only
  useEffect(() => {
    fetchTimeOffsData(true);
  }, []); // Only run on mount

  const fetchTimeOffsData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setMonthLoading(true);
      }
      
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const data = await getTimeOffsData(year, month);
      setTimeOffsData(data.members || []);
      setTotalAbsences(data.totalAbsences || 0);
      setTotalHolidays(data.totalHolidays || 0);
      setTotalApprovedLeaves(data.totalApprovedLeaves || 0);
      setTotalLOP(data.totalLOP || 0);
      setError(null);
    } catch (err) {
      setError('Failed to load time-offs data');
      console.error('Error fetching time-offs data:', err);
    } finally {
      setLoading(false);
      setMonthLoading(false);
    }
  };

  const navigateMonth = async (direction) => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setSelectedMonth(newMonth);
    
    // Fetch data for the new month without triggering useEffect
    await fetchTimeOffsData(false);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Check if we can navigate to next month (not future months)
  const canNavigateNext = () => {
    const nextMonth = new Date(selectedMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const today = new Date();
    return nextMonth.getMonth() <= today.getMonth() && nextMonth.getFullYear() <= today.getFullYear();
  };

  // Check if we can navigate to previous month (not too far back)
  const canNavigatePrevious = () => {
    const prevMonth = new Date(selectedMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const minDate = new Date(2020, 0, 1); // Don't go back further than 2020
    return prevMonth >= minDate;
  };

  const getAbsenceVariant = (absenceCount) => {
    if (absenceCount === 0) return 'success';
    if (absenceCount <= 3) return 'warning';
    return 'error';
  };

  const getAbsenceLabel = (absenceCount) => {
    if (absenceCount === 0) return 'No Absences';
    if (absenceCount === 1) return '1 Day';
    return `${absenceCount} Days`;
  };

  const exportData = () => {
    const csvContent = [
      ['Name', 'Holidays', 'Approved Leaves', 'LOP', 'Total Absences', 'Month'].join(','),
      ...timeOffsData.map(member => [
        member.name,
        member.holidayCount || 0,
        member.approvedLeaveCount || 0,
        member.lopCount || 0,
        member.absenceCount || 0,
        formatMonthYear(selectedMonth)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-offs-${formatMonthYear(selectedMonth).toLowerCase().replace(' ', '-')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="loading-container">
          <BunnyLoader 
            size="large" 
            text="Loading time-offs data..."
            animation="bounce"
          />
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="time-offs-container">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <Button
              variant="neutral-tertiary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/thehub")}
            >
              Back
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-heading-2">Time-Offs Overview</h1>
            </div>
          </div>
          <div className="header-right">
            <Button
              variant="neutral-tertiary"
              icon={<FeatherDownload />}
              onClick={exportData}
              disabled={monthLoading}
            >
              Export
            </Button>
          </div>
        </div>

        {/* Month Selector */}
        <div className="month-selector">
          <Button
            variant="neutral-tertiary"
            icon={<FeatherChevronLeft />}
            onClick={() => navigateMonth(-1)}
            disabled={!canNavigatePrevious() || monthLoading}
          >
            Previous
          </Button>
          <div className="month-display">
            <IconWithBackground 
              icon={<FeatherCalendar />} 
              variant="brand"
            />
            <h2 className="text-heading-3">{formatMonthYear(selectedMonth)}</h2>
          </div>
          <Button
            variant="neutral-tertiary"
            icon={<FeatherChevronRight />}
            onClick={() => navigateMonth(1)}
            disabled={!canNavigateNext() || monthLoading}
          >
            Next
          </Button>
        </div>

        {/* Loading indicator for month changes */}
        {monthLoading && (
          <div className="month-loading-indicator">
            <div className="loading-spinner"></div>
            <span>Loading data for {formatMonthYear(selectedMonth)}...</span>
          </div>
        )}

        {/* Summary Stats */}
        <div className={`summary-stats ${monthLoading ? 'loading' : ''}`}>
          <div className="stat-card">
            <div className="stat-icon">
              <IconWithBackground 
                icon={<FeatherUser />} 
                variant="neutral"
              />
            </div>
            <div className="stat-content">
              <div className="stat-value">{timeOffsData.length}</div>
              <div className="stat-label">Team Members</div>
            </div>
          </div>
          <div className="stat-card stat-holiday">
            <div className="stat-icon">
              <IconWithBackground 
                icon={<FeatherCalendar />} 
                variant="brand"
              />
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalHolidays}</div>
              <div className="stat-label">Holidays</div>
            </div>
          </div>
          <div className="stat-card stat-approved-leave">
            <div className="stat-icon">
              <IconWithBackground 
                icon={<FeatherCalendar />} 
                variant="success"
              />
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalApprovedLeaves}</div>
              <div className="stat-label">Approved Leaves</div>
            </div>
          </div>
          <div className="stat-card stat-lop">
            <div className="stat-icon">
              <IconWithBackground 
                icon={<FeatherClock />} 
                variant="error"
              />
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalLOP}</div>
              <div className="stat-label">LOP</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <IconWithBackground 
                icon={<FeatherClock />} 
                variant="warning"
              />
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalAbsences}</div>
              <div className="stat-label">Total Absences</div>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <div className="error">{error}</div>
          </div>
        )}

        {/* Team Members Grid */}
        <div className={`members-grid ${monthLoading ? 'loading' : ''}`}>
          {timeOffsData.length === 0 ? (
            <div className="empty-state">
              <IconWithBackground 
                icon={<FeatherCalendar />} 
                variant="neutral"
              />
              <h3>No Data Available</h3>
              <p>No time-offs data found for {formatMonthYear(selectedMonth)}</p>
            </div>
          ) : (
            timeOffsData.map((member) => (
              <div key={member.id} className="member-card">
                <div className="member-header">
                  <div className="member-info">
                    <Avatar size="medium">
                      {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </Avatar>
                    <div className="member-details">
                      <h3 className="member-name">{member.name}</h3>
                      <span className="member-email">{member.email}</span>
                    </div>
                  </div>
                  <Badge variant={getAbsenceVariant(member.absenceCount)}>
                    {getAbsenceLabel(member.absenceCount)}
                  </Badge>
                </div>
                
                <div className="member-stats">
                  <div className="stat-item">
                    <span className="stat-label">Working Days:</span>
                    <span className="stat-value">{member.workingDays}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Attendance Rate:</span>
                    <span className="stat-value">
                      {member.workingDays > 0 
                        ? Math.round(((member.workingDays - member.absenceCount) / member.workingDays) * 100)
                        : 0}%
                    </span>
                  </div>
                </div>

                {/* Segregated Absence Details */}
                {(member.holidayCount > 0 || member.approvedLeaveCount > 0 || member.lopCount > 0) && (
                  <div className="absence-details">
                    {member.holidayCount > 0 && (
                      <div className="absence-category">
                        <h4 className="absence-category-title holiday-title">
                          Holidays ({member.holidayCount})
                        </h4>
                        <div className="absence-dates">
                          {member.holidayDates && member.holidayDates.map((date, index) => (
                            <span key={index} className="absence-date holiday-date">
                              {new Date(date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {member.approvedLeaveCount > 0 && (
                      <div className="absence-category">
                        <h4 className="absence-category-title approved-leave-title">
                          Approved Leaves ({member.approvedLeaveCount})
                        </h4>
                        <div className="absence-dates">
                          {member.approvedLeaveDates && member.approvedLeaveDates.map((date, index) => (
                            <span key={index} className="absence-date approved-leave-date">
                              {new Date(date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {member.lopCount > 0 && (
                      <div className="absence-category">
                        <h4 className="absence-category-title lop-title">
                          LOP ({member.lopCount})
                        </h4>
                        <div className="absence-dates">
                          {member.lopDates && member.lopDates.map((date, index) => (
                            <span key={index} className="absence-date lop-date">
                              {new Date(date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default TimeOffs; 